/**
 * Hook personnalisé pour remplacer useSession de NextAuth
 * Fournit une interface compatible avec l'ancienne API
 */

import { OidcUserStatus, useOidc, useOidcAccessToken, useOidcIdToken, useOidcUser } from '@axa-fr/react-oidc'
import { useMemo } from 'react'
import { OidcSession, OidcStatus } from './oidc-config'

interface UseOidcSessionReturn {
    data: OidcSession | null
    status: OidcStatus
}

/**
 * Hook de compatibilité pour remplacer useSession de next-auth
 * Retourne les mêmes propriétés que l'ancien hook
 */
export function useOidcSession(): UseOidcSessionReturn {
    const { isAuthenticated, login, logout } = useOidc()
    const { accessToken, accessTokenPayload } = useOidcAccessToken()
    const { idToken, idTokenPayload } = useOidcIdToken()
    const { oidcUser, oidcUserLoadingState } = useOidcUser()

    const status: OidcStatus = useMemo(() => {
        if (oidcUserLoadingState === OidcUserStatus.Loading) {
            return 'loading'
        }
        return isAuthenticated ? 'authenticated' : 'unauthenticated'
    }, [isAuthenticated, oidcUserLoadingState])

    const data: OidcSession | null = useMemo(() => {
        if (!isAuthenticated || !accessToken) {
            return null
        }

        return {
            user: {
                name: oidcUser?.name || idTokenPayload?.name || null,
                email: oidcUser?.email || idTokenPayload?.preferred_username || null,
                id: idTokenPayload?.oid || idTokenPayload?.sub || null,
            },
            accessToken: accessToken,
            idToken: idToken,
        }
    }, [isAuthenticated, accessToken, idToken, oidcUser, idTokenPayload])

    return { data, status }
}

/**
 * Hook pour obtenir les fonctions de login/logout
 */
export function useOidcAuth() {
    const { login, logout, isAuthenticated } = useOidc()
    const { idToken } = useOidcIdToken()

    return {
        login,
        logout,
        isAuthenticated,
        idToken,
    }
}
